import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import ReactSignatureCanvas from "react-signature-canvas";
import Logo from "components/Home/Logo/Logo";
import { logo1 } from "utils/images";
import { logo2 } from "utils/images";
import { logo3 } from "utils/images";
import userResources from "api/userResources";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { FaPhone } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { IoMdDoneAll } from "react-icons/io";

export default function CreditCardAuthorizationForm() {
  const location = useLocation();
  const updateData = location.state;
  const [airlineData, setAirlineData] = useState([]);
  const [disable, setDisable] = useState(false);
  const [formData, setFormData] = useState({
    holder_name: updateData?.holder_name || "",
    type: updateData?.type || "",
    card_number: updateData?.card_number || "",
    expire_date: updateData?.expire_date || "",
    cvv: updateData?.cvv || "",
    amount: updateData?.amount || "",
    airLine_id: updateData?.airLine_id || "",
    travel_date: updateData?.travel_date || "",
    transportation: updateData?.transportation || "",
    country: updateData?.country || "",
    city: updateData?.city || "",
    state: updateData?.state || "",
    postal_code: updateData?.postal_code || "",
    phone_number: updateData?.phone_number || "",
    signature: "",
    date: updateData?.created_at
      ? updateData?.created_at.split("T")[0] // Extract the date part before the "T"
      : new Date().toISOString().split("T")[0], // Use current date if updateData?.created_at is empty or undefined
  });

  const [errors, setErrors] = useState({});
  const signaturePadRef = useRef();

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    const newValue = type === "file" ? files[0] : value;

    setFormData({
      ...formData,
      [name]: newValue,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  const handleClearSignature = () => {
    signaturePadRef.current.clear(); // Clear the signature canvas
    setFormData((prev) => {
      const { signature, ...rest } = prev; // Destructure to exclude the `signature` key
      return rest; // Return the rest of the object without `signature`
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior

    const newErrors = {};

    if (!updateData) {
      // Validate signature
      if (!formData.signature) {
        newErrors.signature = true;
        toast.warning("Kindly save signature before submission");
      }
    }

    // Validate phone number
    if (!formData.phone_number) {
      newErrors.phone_number = true;
    }

    // Validate other form fields
    Object.entries(formData).forEach(([key, value]) => {
      if (
        !["signature", "phone_number"].includes(key) &&
        (value === "" || value === null)
      ) {
        newErrors[key] = true;
      }
    });

    // Update the errors state
    setErrors(newErrors);

    // If there are any errors, stop submission
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setDisable(true);

    // If no errors, proceed to form submission
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      // Add the id from updateData if available
      if (updateData) {
        formDataToSend.append("id", updateData?.id);
      }

      // API call
      await userResources.creditCard(formDataToSend);
      updateData
        ? toast.success("Form updated successfully!")
        : toast.success("Form submitted successfully!");

      // Reset formData and errors
      setFormData({
        holder_name: "",
        type: "",
        card_number: "",
        expire_date: "",
        cvv: "",
        amount: "",
        airLine_id: "",
        travel_date: "",
        transportation: "",
        country: "",
        city: "",
        state: "",
        postal_code: "",
        phone_number: "",
        signature: null,
        date: "",
      });
      setErrors({});
      handleClearSignature(); // Clear signature field
      setDisable(false);
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      // Extract server error message if present
      const serverErrorMessage =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : "An error occurred while submitting the form.";
      toast.error(`Server error: ${serverErrorMessage}`);
      setDisable(false);
    }
    setDisable(false);
  };

  const saveSignature = () => {
    const signatureDataURL = signaturePadRef.current.toDataURL("image/png");
    const byteString = atob(signatureDataURL.split(",")[1]);
    const mimeString = signatureDataURL
      .split(",")[0]
      .split(":")[1]
      .split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], "signature.png", { type: mimeString });
    console.log(file, "file");

    if (file) {
      setFormData((prev) => ({
        ...prev,
        signature: file,
      }));
    }
  };

  const getAirlineData = async () => {
    try {
      const response = await userResources.airlines();
      setAirlineData(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Handle select field change
  const handleSelectChange = (e) => {
    setFormData({ ...formData, airLine_id: e.target.value });
    setErrors((prevErrors) => {
      const { airLine_id, ...remainingErrors } = prevErrors;
      return remainingErrors;
    });
  };

  useEffect(() => {
    getAirlineData();
  }, []);

  useEffect(() => {
    if (updateData) {
      setFormData((prev) => {
        const { signature, ...rest } = prev; // Destructure and exclude the signature field
        return { ...rest }; // Return the updated object without signature
      });
    }
  }, [updateData]);

  // useEffect(() => {
  //   if (updateData?.signature && signaturePadRef.current) {
  //     signaturePadRef.current.fromDataURL(updateData?.signature);
  //   }
  // }, [updateData?.signature]);
  return (
    <section className="bg-white pt-4 text-gray-800 sm:px-4">
      <ScrollToTopOnRouteChange />

      <div className="m-auto max-w-8xl">
        <div className="container mx-auto px-2 py-6">
          <Logo logo1={logo1} logo2={logo2} logo3={logo3} />

          <h2 className="mb-6 mt-2 text-center text-2xl font-bold">
            Confidential: Credit Card Authorization Form
          </h2>
          <p className="mb-4">
            Date:
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={`ml-2 border-b ${
                errors.date ? "border-red-500" : "border-gray-400"
              } bg-white outline-none ${updateData ? "text-gray-400" : ""}`}
              disabled={updateData ? true : false}
              min={new Date().toISOString().split("T")[0]} // Disable all previous dates
            />
          </p>
          <p className="mb-4">
            IN LIEU OF MY CREDTT CARD IMPRINT, (CREDIT CARD HOLDER'S NAME)
            <br />
            <input
              type="text"
              id="holder_name"
              name="holder_name"
              value={formData.holder_name}
              onChange={handleChange}
              placeholder="Credit Card Holder's Name"
              className={`mt-4 w-full border-b ${
                errors.holder_name ? "border-red-500" : "border-gray-400"
              } bg-white outline-none`}
            />
          </p>
          <div>
            <p className="font-bold">
              HEREBY AUTHORIZE: KATARIA TOURS & TRAVEL
            </p>
            <p>
              TO CHARGE MY (CREDIT CARD /MASTERCARD/AMEX NO.) (EXP DATEI) (3/4
              DIGIT)
            </p>
          </div>
          <p className="mb-4">
            CREDIT CARD TYPE:
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={`mx-2 border-b ${
                errors.type ? "border-red-500" : "border-gray-400"
              } bg-white outline-none`}
            >
              <option value="">Select Card Type</option>
              <option value="MasterCard">MasterCard</option>
              <option value="American Express">American Express</option>
              <option value="Visa">Visa</option>
            </select>
          </p>
          <div className="mb-4 flex flex-wrap items-center gap-4">
            {/* Card Number */}
            <div className="flex items-center">
              <label className="mr-2 whitespace-nowrap">CARD NUMBER:</label>
              <input
                id="card_number"
                type="password"
                name="card_number"
                value={formData.card_number}
                onChange={handleChange}
                className={`w-64 border-b ${
                  errors.card_number ? "border-red-500" : "border-gray-400"
                } bg-white outline-none`}
                style={{ borderRadius: "0px" }}
              />
            </div>

            {/* Expiry Date */}
            <div className="flex items-center">
              <label className="mr-2 whitespace-nowrap">EXPIRY DATE:</label>
              <input
                type="text"
                id="expire_date"
                name="expire_date"
                value={formData.expire_date}
                onChange={handleChange}
                placeholder="MM/YY"
                className={`w-20 border-b ${
                  errors.expire_date ? "border-red-500" : "border-gray-400"
                } bg-white outline-none`}
              />
            </div>

            {/* CVV */}
            <div className="flex items-center">
              <label className="mr-2 whitespace-nowrap">CVV:</label>
              <input
                type="password"
                id="cvv"
                name="cvv"
                value={formData.cvv}
                onChange={handleChange}
                placeholder="CVV"
                className={`w-16 rounded-none border-b ${
                  errors.cvv ? "border-red-500" : "border-gray-400"
                } bg-white outline-none`}
                style={{ borderRadius: "0px" }}
              />
            </div>
          </div>

          <p className="mb-4">
            IN THE CAD DOLLARS AMOUNT OF $
            <input
              type="text"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              className={`mx-2 w-32 border-b ${
                errors.amount ? "border-red-500" : "border-gray-400"
              } bg-white outline-none`}
            />
          </p>
          <p className="mb-4">
            AIRLINE:
            {/* Select Field */}
            <select
              id="airLine_id"
              name="airLine_id"
              value={formData.airLine_id}
              onChange={handleSelectChange}
              className={`mx-2 w-64 border-b ${
                errors.airLine_id ? "border-red-500" : "border-gray-400"
              } bg-white outline-none`}
            >
              <option value="">Select Airline</option>
              {airlineData.map((airline) => (
                <option key={airline.id} value={airline.id}>
                  {airline.airline_name}
                </option>
              ))}
            </select>
            TRAVEL DATE:
            <input
              type="date"
              id="travel_date"
              name="travel_date"
              value={formData.travel_date}
              onChange={handleChange}
              className={`mx-2 border-b ${
                errors.travel_date ? "border-red-500" : "border-gray-400"
              } bg-white outline-none`}
              min={new Date().toISOString().split("T")[0]} // Disable all previous dates
            />
          </p>
          <p className="mb-4 w-full">
            FOR PAYMENT OF TRANSPORTATION FOR MYSELF AND/OR FULL NAME OF
            PASSENGER IF OTHER THAN THE CARD HOLDER
            <input
              type="text"
              id="transportation"
              name="transportation"
              value={formData.transportation}
              onChange={handleChange}
              className={`mt-4 w-full border-b ${
                errors.transportation ? "border-red-500" : "border-gray-400"
              } bg-white outline-none`}
            />
          </p>
          <p>CREDIT CARD BILLING ADDRESS FOR CREDIT CARD VERIFICATION</p>
          <br />
          <div className="flex flex-wrap gap-2">
            <input
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={` border-b ${
                errors.country ? "border-red-500" : "border-gray-400"
              } bg-white outline-none`}
            ></input>
            <div>
              <p>CITY</p>
              <input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={` border-b ${
                  errors.city ? "border-red-500" : "border-gray-400"
                } bg-white outline-none`}
              ></input>
            </div>
            <div>
              <p>STATE</p>
              <input
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`border-b ${
                  errors.state ? "border-red-500" : "border-gray-400"
                } bg-white outline-none`}
              ></input>
            </div>
          </div>
          <div className="grid grid-cols-1 items-start justify-between">
            <div>
              <div className="mt-4 flex flex-wrap gap-2">
                <p>POSTAL CODE</p>
                <input
                  type="number"
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  className={`border-b ${
                    errors.postal_code ? "border-red-500" : "border-gray-400"
                  } bg-white outline-none`}
                  style={{ borderRadius: "0px" }}
                />
              </div>
              <br />
              <div>
                <label
                  htmlFor="phone"
                  className="my-2 block text-sm font-medium text-gray-900 dark:text-[#bb8e25]"
                >
                  Phone Number
                </label>
                <div className="flex">
                  <PhoneInputWithCountrySelect
                    defaultCountry="CA"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={(value) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        phone_number: value, // Update the phone number in formData
                      }));
                      if (value) {
                        // Remove the 'phone_number' key from errors
                        setErrors((prevErrors) => {
                          const { phone_number, ...remainingErrors } =
                            prevErrors;
                          return remainingErrors; // Return the updated errors object without 'phone_number'
                        });
                      }
                    }}
                    placeholder="Enter Phone Number"
                    className={`border-b ${
                      errors.phone_number ? "border-red-500" : "border-gray-400"
                    } bg-white outline-none`}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 w-full">
              <p>SIGNATURE</p>
              <ReactSignatureCanvas
                ref={signaturePadRef}
                canvasProps={{
                  className: `signature-canvas border ${
                    errors.signature ? "border-red-500" : "border-gray-400"
                  }`,
                  width: 350,
                  height: 150,
                  backgroundColor: "transparent",
                }}
              />

              <div
                className={`flex flex-wrap items-center ${
                  updateData ? "justify-end" : "justify-between"
                } sm:justify-between`}
              >
                <button
                  type="button"
                  onClick={handleClearSignature}
                  className="mt-2 bg-red-500 px-4 py-2 text-white"
                >
                  Clear Signature
                </button>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {updateData && (
                    <p className="w-64 text-xs">
                      If you do not wish to update the signature, please leave
                      the signature field empty and avoid saving it.
                    </p>
                  )}
                  {formData.signature ? (
                    <IoMdDoneAll className="text-green-500" size={25} />
                  ) : (
                    <button
                      type="button"
                      className="mt-2 bg-green-500 px-4 py-2 text-white"
                      onClick={() => saveSignature()} // Use onClick for button elements
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
              <p>
                NOTE: By completing and signing this authorization form, the
                cardholder confirms that all the above information has been
                provided voluntarily and accurately on their behalf and
                authorizes Kataria Tours & Travel to use this information for
                the purpose of processing payment for travel services.
              </p>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={disable}
                className={`${
                  disable ? "bg-gray-400" : "bg-[#bb8e25] hover:bg-[#8f7029]"
                } mt-2 w-full  px-4 py-2 text-white `}
              >
                {updateData ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
